"use client"

import { useState, useEffect, useRef, useMemo, type ReactNode } from "react"
import Image from "next/image"
import { useTranslations } from "next-intl"
import { motion, AnimatePresence, useReducedMotion } from "framer-motion"
import { Heart, ArrowRight } from "lucide-react"
import { GlassCard } from "@/components/glass-card"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Link } from "@/i18n/navigation"
import { AnimatedSection } from "../animated-section"
import { useProjects } from "@/app/[locale]/projects/_api/use-project-queries"
import { CURATED_PROJECTS, type ProjectItem } from "./curated-projects"

/** Fisher-Yates shuffle (returns a new array; used so the spotlight order isn't fixed). */
function shuffle<T>(items: readonly T[]): T[] {
  const a = [...items]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j]!, a[i]!]
  }
  return a
}

function useProjectData() {
  // The spotlight pool is the hand-curated STATIC selection (see curated-projects.ts) —
  // deterministic and always full, independent of pagination/sort. We still fetch a batch
  // of projects, but only to derive the category chips shown in the left CTA panel.
  const { data } = useProjects({ size: 50, page: 0 })
  const uniqueFields = useMemo(() => {
    const allFields = (data?.content ?? []).flatMap((p) => p.fieldsOfWork ?? [])
    return Array.from(new Map(allFields.map((f) => [f.id, f])).values())
  }, [data])
  return { projects: CURATED_PROJECTS, isLoading: false, uniqueFields }
}

function Header({ hideSub }: { hideSub?: boolean }) {
  const t = useTranslations("start.projects")
  return (
    <AnimatedSection>
      <div className="mb-12 text-center">
        <h2 className="mb-4 text-3xl font-bold md:text-4xl">{t("title")}</h2>
        {!hideSub && <p className="mx-auto max-w-2xl text-lg text-muted-foreground">{t("sub")}</p>}
      </div>
    </AnimatedSection>
  )
}

/** Cover visual: the project's description image (arbitrary external hosts → plain
 *  <img>, not next/image). Falls back to a brand-gradient + logo when absent. */
function CoverImage({ p, className }: { p: ProjectItem; className?: string }) {
  if (p.imageUrl) {
    // eslint-disable-next-line @next/next/no-img-element
    return <img src={p.imageUrl} alt={p.name} loading="lazy" className={`h-full w-full object-cover ${className ?? ""}`} />
  }
  return (
    <div
      className={`flex h-full w-full items-center justify-center ${className ?? ""}`}
      style={{ background: "linear-gradient(135deg, oklch(0.52 0.17 143 / 0.20), oklch(0.84 0.16 87 / 0.16))" }}
    >
      <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-2xl bg-white/90 shadow-md">
        {p.logoUrl ? (
          <Image src={p.logoUrl} alt={p.name} width={80} height={80} className="max-h-full max-w-full object-contain p-2" />
        ) : (
          <Heart className="h-8 w-8 text-primary" />
        )}
      </div>
    </div>
  )
}

/** Small logo shown left of the title. */
function LogoChip({ p }: { p: ProjectItem }) {
  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white/90 shadow-sm ring-1 ring-black/5">
      {p.logoUrl ? (
        <Image src={p.logoUrl} alt={p.name} width={40} height={40} className="max-h-full max-w-full object-contain p-1" />
      ) : (
        <Heart className="h-5 w-5 text-primary" />
      )}
    </div>
  )
}

/** Logo-left-of-title row + (optional) org line. `light` for dark overlays. */
function TitleBlock({ p, light }: { p: ProjectItem; light?: boolean }) {
  return (
    <>
      <div className="flex items-center gap-3">
        <LogoChip p={p} />
        <div className="min-w-0">
          {/* Tag line is always rendered (nbsp fallback) and the name is clamped to one
              line so the title block keeps a constant height — no shift as projects rotate. */}
          <p className={`truncate text-xs font-medium uppercase tracking-wide ${light ? "text-white/80" : "text-primary"}`}>{p.tag || " "}</p>
          <h3 className={`line-clamp-1 text-lg font-bold leading-tight ${light ? "text-white" : ""}`}>{p.name}</h3>
        </div>
      </div>
    </>
  )
}

/* ── Big-card layouts ──────────────────────────────────────────────────── */

/** spotlightA — cover image on top, logo+title row below. */
function BigCardTop({ p }: { p: ProjectItem }) {
  return (
    <GlassCard className="flex h-full flex-col overflow-hidden border-0 p-0 transition-all hover:shadow-2xl">
      <div className="min-h-0 flex-1 overflow-hidden">
        <CoverImage p={p} />
      </div>
      <div className="shrink-0 p-5">
        <TitleBlock p={p} />
      </div>
    </GlassCard>
  )
}

/* ── Spotlight base (left CTA panel + one rotating big card) ────────────── */

const SPOTLIGHT_INTERVAL = 7000

function ProjectsSpotlightBase({ renderCard }: { renderCard: (p: ProjectItem) => ReactNode }) {
  const t = useTranslations("start.projects")
  const { projects, isLoading, uniqueFields } = useProjectData()
  const reduce = useReducedMotion()
  const [index, setIndex] = useState(0)
  const hovered = useRef(false)

  // Shuffle the curated projects so the spotlight order is never fixed/alphabetical.
  const pool = useMemo(() => shuffle(projects).slice(0, 12), [projects])

  useEffect(() => {
    if (reduce || pool.length <= 1) return
    const id = setInterval(() => {
      if (!hovered.current) setIndex((p) => (p + 1) % pool.length)
    }, SPOTLIGHT_INTERVAL)
    return () => clearInterval(id)
  }, [reduce, pool.length])

  const current = pool.length > 0 ? pool[index % pool.length]! : undefined

  return (
    <section className="py-16">
      <div className="container mx-auto px-4">
        <div className="mx-auto max-w-7xl">
          <Header hideSub />
          {isLoading ? (
            <Skeleton className="h-80 rounded-2xl" />
          ) : (
            <AnimatedSection>
              <div className="grid items-stretch gap-6 md:grid-cols-2">
                {/* Left: gradient info / CTA panel */}
                <div
                  className="flex flex-col justify-center rounded-2xl p-6 md:p-8"
                  style={{ background: "linear-gradient(135deg, oklch(0.52 0.17 143 / 0.85), oklch(0.45 0.14 143 / 0.95))" }}
                >
                  <h3 className="text-2xl font-bold text-white md:text-3xl">
                    {t("doGoodTogether")}
                  </h3>
                  <p className="mt-3 text-white/80">{t("spotlightCopy")}</p>
                  <p className="mt-3 text-sm text-white/70">{t("transparency")}</p>
                  {uniqueFields.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {uniqueFields.slice(0, 8).map((f) => (
                        <Link
                          key={f.id}
                          href={`/projects?category=${f.id}`}
                          className="rounded-full bg-white/20 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-white/35"
                        >
                          {f.title}
                        </Link>
                      ))}
                      {uniqueFields.length > 8 && (
                        <Link
                          href="/projects"
                          className="rounded-full border border-white/40 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-white/20"
                        >
                          {t("more", { count: uniqueFields.length - 8 })}
                        </Link>
                      )}
                    </div>
                  )}
                  <div className="mt-5">
                    <Button size="lg" className="rounded-xl bg-white text-primary shadow-md hover:bg-white/90" asChild>
                      <Link href="/projects">
                        {t("browse")}
                        <ArrowRight className="ml-1 h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                </div>
                {/* Right: one big rotating project card, equal height to the panel (pause on hover) */}
                <div
                  className="relative h-80 md:h-full md:min-h-[20rem]"
                  onMouseEnter={() => { hovered.current = true }}
                  onMouseLeave={() => { hovered.current = false }}
                >
                  <AnimatePresence mode="wait">
                    {current && (
                      <motion.div
                        key={current.id}
                        className="absolute inset-0"
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.98 }}
                        transition={{ duration: 0.5, ease: "easeInOut" }}
                      >
                        <Link href={`/projects/${current.slug}`} className="block h-full">
                          {renderCard(current)}
                        </Link>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </AnimatedSection>
          )}
        </div>
      </div>
    </section>
  )
}

export function ProjectsSpotlightA() {
  return <ProjectsSpotlightBase renderCard={(p) => <BigCardTop p={p} />} />
}
